// crewbit-admin/src/components/schedule/DaySingleColumn.tsx
//
// Schedule Module Phase C C1.5 — single-resource layout for the Day canvas.
// Body parallel to mobile crewbit-mobile/components/schedule/DaySingleColumn.tsx
// modulo runtime per feedback_admin_pair_must_match_mobile_body.md.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §2.3 + §2.6.

import { Fragment, useEffect, useRef, useState } from 'react';
import { palette, admin } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';
import { EntryTile } from './EntryTile';
import { TravelSliver } from './TravelSliver';
import NowIndicator from './NowIndicator';

const DEFAULT_DAY_START_HOUR = 7;
const DEFAULT_DAY_END_HOUR = 19;

export interface DaySingleColumnProps {
  date: string;
  entries: ScheduleEntry[];
  travelBufferMinutes: number;
  onEntryTap: (entryId: string) => void;
  onEntryLongPress?: (entryId: string) => void;
  showTravelSlivers?: boolean;
  industryAccent?: string;
  dayStartHour?: number;
  dayEndHour?: number;
}

function isToday(iso: string): boolean {
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return iso === todayIso;
}

export function DaySingleColumn({
  date,
  entries,
  travelBufferMinutes,
  onEntryTap,
  onEntryLongPress,
  showTravelSlivers,
  industryAccent,
  dayStartHour = DEFAULT_DAY_START_HOUR,
  dayEndHour = DEFAULT_DAY_END_HOUR,
}: DaySingleColumnProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const sorted = [...entries].sort((a, b) => a.start_at.localeCompare(b.start_at));
  const showNow = isToday(date);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => setContainerHeight(el.clientHeight));
    ro.observe(el);
    setContainerHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        flex: 1,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        overflowY: 'auto',
        backgroundColor: admin.bg,
      }}
    >
      {sorted.length === 0 ? (
        <div style={{ fontSize: 14, color: palette.g500, textAlign: 'center', padding: '48px 0' }}>
          No entries today
        </div>
      ) : (
        sorted.map((entry, idx) => {
          const next = sorted[idx + 1];
          const sliverEligible =
            showTravelSlivers === true &&
            next != null &&
            entry.location_lat != null &&
            entry.location_lng != null &&
            next.location_lat != null &&
            next.location_lng != null;
          return (
            <Fragment key={entry.id}>
              <EntryTile
                entry={entry}
                onTap={onEntryTap}
                onLongPress={onEntryLongPress}
                density="normal"
                industryAccent={industryAccent}
              />
              {sliverEligible ? (
                <TravelSliver
                  fromEntry={entry}
                  toEntry={next}
                  travelBufferMinutes={travelBufferMinutes}
                />
              ) : null}
            </Fragment>
          );
        })
      )}
      {showNow && containerHeight > 0 ? (
        <NowIndicator
          containerHeight={containerHeight}
          dayStartHour={dayStartHour}
          dayEndHour={dayEndHour}
        />
      ) : null}
    </div>
  );
}
